import {
  Box,
  Button,
  ButtonProps,
  Grid,
  Typography,
  styled,
} from "@mui/material";
import * as React from "react";
import MoneyBillsIcon from "../../../components/Vectors/MoneyBillsIcon";
import ChartOpenFinanceIcon from "../../../components/Vectors/ChartOpenFinanceIcon";
import HomeInvoiceIcon from "../../../components/Vectors/HomeInvoiceIcon";
import { Link } from "react-router-dom";
import { useAppSelector } from "../../../hooks/hooks";
import { IProfileState } from "../../../store/profileSlice";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
const StyledLink = styled(Link)`
  text-decoration: none;
  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

const StyledButton = styled(Button)<
  ButtonProps & { component: React.ElementType }
>`
  &:hover {
    color: white;
    text-decoration: none;
    background-color: #503dfc;
    box-shadow: none;
  }
`;

interface ItemProps {
  icon?: React.ReactNode;
  to?: string;
  children?: React.ReactNode;
  extraContent?: React.ReactNode;
  disabled?: boolean;
  textButton?: string;
}

const Item: React.FC<ItemProps> = ({
  icon,
  to,
  children,
  disabled,
  extraContent,
  textButton,
}) => {
  return (
    <Grid
      width={"100%"}
      item
      container
      xs={12}
      sx={{
        border: "1px solid grey",
        borderRadius: "8px",
        borderColor: "#E9EBF0",
        mb: 4,
        pr: 2,
      }}
    >
      <Grid
        item
        xs={2}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Box width={"70px"} height={"70px"}>
          {icon}
        </Box>
      </Grid>
      <Grid item xs={7} sx={{ my: 6 }}>
        {children}
      </Grid>
      <Grid
        item
        xs={3}
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
      >
        <Box>
          <StyledButton
            variant="outlined"
            sx={{
              textTransform: "none",
              borderRadius: 50,
              mx: 4,
              ml: 4,
              py: "8px",
              color: "primary.main",
              borderColor: "primary.main",
              width: "200px",
            }}
            component={StyledLink}
            {...{ to: to }}
            disabled={disabled}
          >
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 14,
              }}
              color="inherit"
              noWrap
            >
              {textButton}
            </Typography>
          </StyledButton>
        </Box>
      </Grid>
      {extraContent}
    </Grid>
  );
};

interface SimulateAnticipationTooltipItemProps {
  icon?: React.ReactNode;
  text?: string;
  showArrowIcon?: boolean;
}

const SimulateAnticipationTooltipItem: React.FC<
  SimulateAnticipationTooltipItemProps
> = ({ icon, text, showArrowIcon }) => {
  return (
    <Grid item xs={2}>
      <Box display="flex" flexDirection="row" textAlign="center">
        <Box
          sx={{
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box>{icon}</Box>
          <Typography sx={{ mt: 1 }} variant={"caption"}>
            {text}
          </Typography>
        </Box>
        {showArrowIcon ? (
          <ArrowForwardIcon
            sx={{ color: "#69708A", opacity: 0.3, mt: 3, mx: 2 }}
          />
        ) : (
          <Box width={"60px"} />
        )}
      </Box>
    </Grid>
  );
};

const SimulateAnticipationTooltip: React.FC = () => {
  return (
    <Box>
      <Box>
        <Typography pl={6} variant="h6" color={"#69708A"}>
          Veja como é fácil importar suas notas
        </Typography>
        <Grid
          mt={4}
          pb={4}
          justifyContent={"center"}
          display="flex"
          flexDirection={"row"}
          width={"100%"}
          container
        >
          <SimulateAnticipationTooltipItem
            icon={<UploadFileIcon sx={{ color: "#69708A", fontSize: 40 }} />}
            text={"Envie as Notas Fiscais"}
            showArrowIcon={true}
          />
          <SimulateAnticipationTooltipItem
            icon={
              <PlaylistAddCheckIcon sx={{ color: "#69708A", fontSize: 40 }} />
            }
            text={"Simule a antecipação"}
            showArrowIcon={true}
          />
          <SimulateAnticipationTooltipItem
            icon={
              <MonetizationOnOutlinedIcon
                sx={{ color: "#69708A", fontSize: 40 }}
              />
            }
            text={"Analise a proposta"}
            showArrowIcon={true}
          />
          <SimulateAnticipationTooltipItem
            icon={
              <EditNoteOutlinedIcon sx={{ color: "#69708A", fontSize: 40 }} />
            }
            text={"Assine o contrato"}
            showArrowIcon={true}
          />
          <SimulateAnticipationTooltipItem
            icon={
              <CalendarMonthOutlinedIcon
                sx={{ color: "#69708A", fontSize: 40 }}
              />
            }
            text={"Receba o dinheiro em sua conta"}
            showArrowIcon={false}
          />
        </Grid>
      </Box>
    </Box>
  );
};

const HomePage: React.FC = () => {
  const profile: IProfileState = useAppSelector((state) => state.profileSlice);

  return (
    <div>
      <Box sx={{ px: 10 }}>
        <Box sx={{ pt: 6, pb: 4 }}>
          <Typography variant="h1" color="inherit" noWrap>
            Olá, {profile.profile?.person?.name.split(" ")[0]}
          </Typography>
          <Typography variant="h5" color="inherit" noWrap>
            Conheça nossos produtos e serviços
          </Typography>
        </Box>
        <Item
          icon={<HomeInvoiceIcon />}
          to="receivable"
          textButton="simular gratuitamente"
          extraContent={<SimulateAnticipationTooltip />}
        >
          <Box>
            <Typography variant="h4" color="inherit">
              Simular antecipação de vendas
            </Typography>
            <Typography sx={{ mt: 1 }} variant="body1" color="inherit">
              Importe suas notas fiscais para simular gratuitamente a
              antecipação de parcelas/recebíveis.
            </Typography>
          </Box>
        </Item>
        <Item
          icon={<MoneyBillsIcon />}
          to="anticipation"
          textButton="ver antecipações"
        >
          <Box>
            <Typography variant="h4" color="inherit">
              Antecipações
            </Typography>
            <Typography sx={{ mt: 1 }} variant="body1" color="inherit">
              Acompanhe suas antecipações já contratadas de maneira simples e
              rápida.
            </Typography>
          </Box>
        </Item>
        <Item
          icon={<ChartOpenFinanceIcon />}
          to="consents"
          textButton="acessar open finance"
        >
          <Box>
            <Typography variant="h4">Portal Open Finance</Typography>
            <Typography sx={{ mt: 1 }} variant="body1">
              Se conecte ao mundo Open Finance e tome as melhores decisões
              baseadas em dados!
            </Typography>
          </Box>
        </Item>
      </Box>
    </div>
  );
};

export default HomePage;
